/**
 * Version Service
 *
 * Detects backend and SPA version changes to prompt users to reload when updates are deployed.
 * This solves the caching issue where users may be running stale SPA code after deployments.
 *
 * Backend Detection: Checks X-Api-Version header from API responses
 * SPA Detection: Checks version.json file that is generated at build time
 */

import {Button} from 'src/design/atoms/Button';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {notifications} from '@mantine/notifications';

const API_VERSION_HEADER = 'x-api-version';
const API_VERSION_KEY = 'proact_api_version';
const UPDATE_DISMISSED_KEY = 'proact_update_dismissed';
const UPDATE_NOTIFICATION_ID = 'version-update-notification';

// Build-time SPA version - injected by Vite at build time
const BUILD_SPA_VERSION = import.meta.env.VITE_BUILD_VERSION as
  | string
  | undefined;

let updateNotificationShown = false;
let apiVersionInitialized = false;

// A waiting worker may be a deploy behind, so unregister instead of activate:
// the reload then hits the network and registers a fresh, latest worker.
export const unregisterServiceWorkerAndReload = async (): Promise<void> => {
  clearVersionState();

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
  } catch {
    // Reload regardless; the version check will re-prompt if still stale.
  }
  window.location.reload();
};

/**
 * Shows a persistent notification prompting the user to reload
 */
const showUpdateNotification = (
  source: 'api' | 'spa' | 'sw',
  onReload: () => void | Promise<unknown> = () => {
    void unregisterServiceWorkerAndReload();
  }
) => {
  // Don't show multiple notifications
  if (updateNotificationShown) return;

  // Check if user recently dismissed the notification (within last 5 minutes)
  const dismissedAt = sessionStorage.getItem(UPDATE_DISMISSED_KEY);
  if (dismissedAt) {
    const dismissedTime = parseInt(dismissedAt, 10);
    const fiveMinutes = 5 * 60 * 1000;
    if (Date.now() - dismissedTime < fiveMinutes) {
      return;
    }
  }

  updateNotificationShown = true;

  const messageText =
    source === 'api'
      ? 'A new version of Proact has been deployed. Please reload to get the latest updates.'
      : 'A new version of Proact is available. Please reload to get the latest updates.';

  const handleReload = () => {
    void onReload();
  };

  notifications.show({
    id: UPDATE_NOTIFICATION_ID,
    title: 'Update Available',
    message: (
      <Stack gap="xs">
        <Text size="sm">{messageText}</Text>
        <Button
          w="fit-content"
          size="xs"
          color="blue"
          onClick={handleReload}>
          Reload Now
        </Button>
      </Stack>
    ),
    color: 'blue',
    autoClose: false,
    withCloseButton: true,
    onClose: () => {
      sessionStorage.setItem(UPDATE_DISMISSED_KEY, Date.now().toString());
      updateNotificationShown = false;
    },
  });
};

/**
 * Checks the API version from a response header.
 * Call this from the axios response interceptor.
 */
export const checkApiVersion = (headers: Record<string, string>) => {
  const version = headers[API_VERSION_HEADER];

  if (!version) return;

  if (!apiVersionInitialized) {
    // First API call after page load — set fresh baseline
    sessionStorage.setItem(API_VERSION_KEY, version);
    apiVersionInitialized = true;
    return;
  }

  const storedVersion = sessionStorage.getItem(API_VERSION_KEY);

  if (storedVersion && storedVersion !== version) {
    // Version changed - backend was updated
    showUpdateNotification('api');
  }
};

/**
 * Checks if the SPA version has changed by fetching version.json
 * Call this on visibility change or periodically
 */
export const checkSpaVersion = async (): Promise<void> => {
  // If no build version is set, skip check (development mode)
  if (!BUILD_SPA_VERSION) return;

  try {
    // Add cache-busting query param to bypass browser cache
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });

    if (!response.ok) return;

    const data = await response.json();
    const serverVersion = data.version as string | undefined;

    if (!serverVersion) return;

    // Compare with build-time version
    if (BUILD_SPA_VERSION !== serverVersion) {
      showUpdateNotification('spa');
    }
  } catch {
    // Silently fail - version check is non-critical
  }
};

/**
 * Prompts the user to reload for a waiting service worker update, via the
 * same notification used for API/SPA version changes. `reload` is the
 * caller-supplied action that activates the waiting worker (e.g. the
 * `updateSW` callback from `virtual:pwa-register`).
 */
export const notifyServiceWorkerUpdate = (
  reload: () => void | Promise<unknown>
): void => {
  showUpdateNotification('sw', reload);
};

/**
 * Clears stored versions (useful for logout)
 */
export const clearVersionState = () => {
  sessionStorage.removeItem(API_VERSION_KEY);
  sessionStorage.removeItem(UPDATE_DISMISSED_KEY);
  updateNotificationShown = false;
  apiVersionInitialized = false;
  notifications.hide(UPDATE_NOTIFICATION_ID);
};
